import {
    Text,
    Flex,
    SimpleGrid,
    HStack,
    Spinner,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Select
} from '@chakra-ui/react'
import { ChangeEvent, useEffect, useState } from 'react'
import { FiCalendar } from 'react-icons/fi'
import DatePickerImport from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

// react-datepicker is a CommonJS module. Under Vite/esbuild its default import
// arrives double-wrapped ({ default: Component }), so rendering <DatePicker>
// throws "Element type is invalid … got: object" and crashes the Report page.
// Defensively unwrap `.default` so it works under both Vite and Next.
const DatePicker =
    (DatePickerImport as { default?: typeof DatePickerImport }).default ??
    DatePickerImport
import ReactSelect from 'react-select'
import { DataTable } from '@/components/datatable/Datatable'
import {
    CheckinStatusFilter,
    useFindAllCheckins
} from '@/modules/teacher/hooks/useFindAllCheckins'
import { useBranchDispatchContext } from '@/modules/teacher/context/BranchContext'
import { BranchType, useBranches } from '@/shared/api/hooks/useBranches'
import { teacherReportPageColumns } from '@/modules/teacher/utils/tableColumnHeader'
import { PROTECTED_ROUTES } from '@/shared/config/routes'
import PageContainer from '@/components/PageContainer'

export default function TeacherReport() {
    const [startDate, setStartDate] = useState(new Date())
    const [ageGroup, setAgeGroup] = useState('')
    const [status, setStatus] = useState<CheckinStatusFilter>('')
    const [branch, setBranch] = useState<BranchType | null>(null)

    // custom hooks
    const dispatch = useBranchDispatchContext()
    const { branches, loading: branchLoading } = useBranches()
    const { data, loading } = useFindAllCheckins({
        startDate,
        branchId: branch ? branch.value : '',
        ageGroup,
        status
    })

    // change handlers
    const handleBranchChange = (option: BranchType | null) => {
        if (!option) {
            setBranch(null)
            return
        }
        if (option) {
            dispatch({
                type: 'set_branch',
                currentBranch: option
            })
            setBranch(option)
        }
    }

    const handleAgeGrpChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setAgeGroup(e.target.value)
    }

    const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setStatus(e.target.value as CheckinStatusFilter)
    }

    useEffect(() => {}, [branch, ageGroup]) // to refetch "data" on change

    return (
        <PageContainer
            routeConfig={PROTECTED_ROUTES['/user/teacher/teacher-report']}
            headerText="Report Check in"
            subHeaderText="Here is a report of check-ins and check-outs for today."
            headerActions={
                <SimpleGrid
                    columns={{ sm: 1, md: 4 }}
                    spacing="2"
                    alignItems="center"
                    p="2"
                >
                    <Flex gap={2} alignItems="center" direction={['column', 'row']}>
                        <Text fontWeight={'bold'}>Filter:</Text>
                        <Select
                            placeholder="Age Group"
                            value={ageGroup}
                            onChange={handleAgeGrpChange}
                        >
                            <option value="Toddlers">Toddlers</option>
                            <option value="PreSchool">Pre School</option>
                            <option value="SchoolAge">School Age</option>
                            <option value="Teenagers">Teenagers</option>
                            <option value="Samuels">Samuels</option>
                        </Select>
                    </Flex>
                    <Select
                        aria-label="Check-in status"
                        placeholder="All children"
                        value={status}
                        onChange={handleStatusChange}
                    >
                        <option value="in">Still checked in</option>
                        <option value="out">Checked out</option>
                    </Select>
                    <ReactSelect
                        isClearable
                        styles={{
                            control: (baseStyles) => ({
                                ...baseStyles,
                                width: '15rem'
                            })
                        }}
                        isLoading={branchLoading}
                        value={branch}
                        isSearchable
                        placeholder="All branches"
                        options={branches}
                        onChange={handleBranchChange}
                    />
                    <HStack p="2" borderWidth="1px" borderRadius="md">
                        <FiCalendar color="brand.500" />
                        <DatePicker
                            selected={startDate}
                            onChange={(date: Date) => setStartDate(date)}
                            dateFormat="d MMMM, yyyy"
                            customInput={
                                <input
                                    style={{
                                        border: 'none',
                                        outline: 'none'
                                    }}
                                />
                            }
                        />
                    </HStack>
                </SimpleGrid>
            }
        >
            <SimpleGrid
                columns={{ base: 1, md: 3 }}
                spacing="4"
                p="6"
                mb={6}
                borderWidth="1px"
                borderRadius="lg"
            >
                <Stat>
                    <StatLabel>Total CheckIn</StatLabel>
                    <StatNumber>{data.checkInAggregates.totalCheckedIn}</StatNumber>
                    <StatHelpText>
                        Date: {`${new Date(startDate).toLocaleDateString('en-GB')}`}
                    </StatHelpText>
                </Stat>
                <Stat>
                    <StatLabel>Still Checked In</StatLabel>
                    <StatNumber>{data.checkInAggregates.totalStillCheckedIn}</StatNumber>
                    <StatHelpText>
                        Date: {`${new Date(startDate).toLocaleDateString('en-GB')}`}
                    </StatHelpText>
                </Stat>
                <Stat>
                    <StatLabel>Total CheckOut</StatLabel>
                    <StatNumber>{data.checkInAggregates.totalCheckedOut}</StatNumber>
                    <StatHelpText>
                        Date: {`${new Date(startDate).toLocaleDateString('en-GB')}`}
                    </StatHelpText>
                </Stat>
            </SimpleGrid>

            {loading ? (
                <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="brand.200"
                    color="brand.500"
                    size="xl"
                />
            ) : (
                <DataTable
                    columns={teacherReportPageColumns}
                    data={data.checkInResults}
                    dataDate={startDate}
                    showDownloadButton
                />
            )}
        </PageContainer>
    )
}
